// src/components/coldcalendar/sections/CommonReportingTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationCircleIcon,
  ArrowsUpDownIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  EyeIcon,
  EyeSlashIcon,
  CircleStackIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import PerformanceFilters from './ReportingFilters';
import { parseValue, getColorClass, tableColumns, getFieldValue } from '../../../utils/dataUtils';
import { useColdCalendarData } from '../../../hooks/useColdCalendarData';

const subtitle1 =
  'Includes contacts not in original invite but part of Yes/Maybe responses, webinar reports, and/or bookings.';
const subtitle2 =
  'Excludes contacts in the Missing category that were not in the original invite.';

// ─── Table config ─────────────────────────────────────────────────────────────

const TABLE_CONFIG = {
  title: {
    group_by_column: 'reporting_title',
    titleLabel: 'Test Title',
    entityName: 'test titles',
    filters: { universal: ['webinar_filter', 'test_filter'], specific: ['version_filter'] },
    hasSpecialFormatting: true,
    loadingMessage: 'Loading title performance data…',
  },
  description: {
    group_by_column: 'reporting_description',
    titleLabel: 'Test Description',
    entityName: 'test descriptions',
    filters: { universal: ['webinar_filter'], specific: ['description_filter'] },
    hasSpecialFormatting: true,
    loadingMessage: 'Loading description performance data…',
  },
  account: {
    group_by_column: 'reporting_email',
    titleLabel: 'Account',
    entityName: 'accounts',
    filters: { universal: ['webinar_filter', 'test_filter'], specific: ['account_filter'] },
    hasSpecialFormatting: true,
    loadingMessage: 'Loading account performance data…',
  },
};

const FILTER_DISPLAY = {
  webinar_filter:     { label: 'Webinars',      color: 'brightblue' },
  test_filter:        { label: 'Test Titles',   color: 'teal' },
  version_filter:     { label: 'Versions',      color: 'violet' },
  description_filter: { label: 'Descriptions', color: 'brand' },
  account_filter:     { label: 'Accounts',      color: 'violet' },
};

// ─── Empty state ──────────────────────────────────────────────────────────────

const EmptyState = React.memo(({ onRefresh, hasFilters, clearFilters, entityName }) => (
  <div className="bg-white border border-grey-200 rounded-xl p-10 text-center">
    <div className="w-12 h-12 bg-grey-100 rounded-full flex items-center justify-center mx-auto mb-4">
      <CircleStackIcon className="w-6 h-6 text-grey-400" />
    </div>
    <h3 className="text-sm font-semibold text-darkblue-500 mb-1">
      {hasFilters ? 'No matching data found' : 'No data available'}
    </h3>
    <p className="text-xs text-grey-500 mb-5 max-w-xs mx-auto">
      {hasFilters
        ? `No ${entityName} match your current filters. Try adjusting or clearing them.`
        : `No performance data is currently available for ${entityName}.`}
    </p>
    <div className="flex items-center justify-center gap-2">
      {hasFilters && (
        <button
          onClick={clearFilters}
          className="px-3 py-1.5 text-xs border border-brightblue-200 text-brightblue-600 rounded-lg hover:bg-brightblue-50 transition-colors"
        >
          Clear filters
        </button>
      )}
      <button
        onClick={onRefresh}
        className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
      >
        <ArrowPathIcon className="w-3.5 h-3.5" />
        Refresh data
      </button>
    </div>
  </div>
));
EmptyState.displayName = 'EmptyState';

// ─── Table row ────────────────────────────────────────────────────────────────

const TableRow = React.memo(({ row, columns, index, config }) => (
  <tr className={index % 2 === 0 ? 'bg-white hover:bg-grey-50' : 'bg-grey-50 hover:bg-grey-100'}>
    {columns.map((col) => {
      let value;
      let special = false;

      if (col.key === 'Title') {
        value = row.Title || 'N/A';
      } else if (
        config.hasSpecialFormatting &&
        col.key === 'Invite sent' &&
        row.Title?.toLowerCase().includes('missing')
      ) {
        value = getFieldValue(row, 'Total Emails');
        special = true;
      } else {
        value = getFieldValue(row, col.key);
      }

      const colorClass = col.type === 'percentage' ? getColorClass(value, col.key) : '';

      return (
        <td
          key={col.key}
          className={`px-2 py-2 text-xs ${
            col.key === 'Title'
              ? 'text-darkblue-500 font-medium text-left'
              : `text-center ${colorClass || 'text-darkblue-500'}`
          }`}
          title={col.key === 'Title' ? value : undefined}
        >
          {col.key === 'Title' ? (
            <div className="truncate font-medium text-darkblue-500">{value}</div>
          ) : (
            <div className="relative group flex items-center justify-center gap-1">
              <span className={special ? 'text-pink-700 font-semibold' : ''}>{value}</span>
              {special && (
                <>
                  <InformationCircleIcon className="w-3 h-3 text-pink-500" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-56 bg-darkblue-500 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none">
                    {subtitle1}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-darkblue-500" />
                  </div>
                </>
              )}
            </div>
          )}
        </td>
      );
    })}
  </tr>
));
TableRow.displayName = 'TableRow';

// ─── Main component ───────────────────────────────────────────────────────────

const ReportingTestTable = ({ tableType }) => {
  const config = TABLE_CONFIG[tableType];
  if (!config) throw new Error(`Invalid table type: ${tableType}`);

  const getInitialFilters = useCallback(() => {
    const base = { group_by_column: config.group_by_column, start_date: '', end_date: '', search_term: '' };
    config.filters.universal.forEach((f) => (base[f] = []));
    config.filters.specific.forEach((f) => (base[f] = []));
    return base;
  }, [config]);

  const [filters, setFilters] = useState(getInitialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showGrandTotal, setShowGrandTotal] = useState(true);

  const { data, filterOptions, loading, refreshing, error, initialized, refetch, lastUpdated } =
    useColdCalendarData(filters);

  const rawData    = data?.rows || [];
  const grandTotal = data?.grand_total || null;

  const displayColumns = useMemo(
    () => tableColumns.map((c) => (c.key === 'Title' ? { ...c, label: config.titleLabel, sortable: true } : c)),
    [config.titleLabel],
  );

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !rawData.length) return rawData;
    const col = displayColumns.find((c) => c.key === sortConfig.key);
    if (!col) return rawData;
    return [...rawData].sort((a, b) => {
      const av = parseValue(getFieldValue(a, sortConfig.key), col.type);
      const bv = parseValue(getFieldValue(b, sortConfig.key), col.type);
      if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawData, sortConfig, displayColumns]);

  const { totalItems, totalPages, currentRows, startIndex } = useMemo(() => {
    const total = sortedData.length;
    const pages = Math.ceil(total / itemsPerPage);
    const start = (currentPage - 1) * itemsPerPage;
    return {
      totalItems: total,
      totalPages: pages,
      currentRows: sortedData.slice(start, start + itemsPerPage),
      startIndex: start,
    };
  }, [sortedData, currentPage, itemsPerPage]);

  const handleSort = useCallback((key) => {
    const col = displayColumns.find((c) => c.key === key);
    if (!col?.sortable) return;
    setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  }, [displayColumns]);

  const getSortIcon = useCallback((key) => {
    if (sortConfig.key !== key) return <ArrowsUpDownIcon className="w-3.5 h-3.5" />;
    return sortConfig.direction === 'asc'
      ? <ArrowUpIcon className="w-3.5 h-3.5" />
      : <ArrowDownIcon className="w-3.5 h-3.5" />;
  }, [sortConfig]);

  const handleFiltersChange = useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => { setFilters(getInitialFilters()); setCurrentPage(1); }, [getInitialFilters]);

  useEffect(() => { setCurrentPage(1); }, [rawData.length]);

  const dataInfo = useMemo(() => ({ totalRows: rawData.length, lastUpdated }), [rawData.length, lastUpdated]);

  const activeFilterCount = useMemo(() => {
    let n = 0;
    [...config.filters.universal, ...config.filters.specific].forEach((f) => { if (filters[f]?.length > 0) n++; });
    if (filters.search_term) n++;
    if (filters.start_date)  n++;
    if (filters.end_date)    n++;
    return n;
  }, [filters, config]);

  const hasActiveFilters = activeFilterCount > 0;

  // ── Initial loading ────────────────────────────────────────────────────────
  if (loading && !initialized && rawData.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-xs text-grey-500">{config.loadingMessage}</p>
      </div>
    );
  }

  // ── Empty state ────────────────────────────────────────────────────────────
  if (rawData.length === 0 && !loading && !refreshing) {
    return (
      <div className="space-y-4">
        <EmptyState onRefresh={refetch} hasFilters={hasActiveFilters} clearFilters={clearFilters} entityName={config.entityName} />
        {error && hasActiveFilters && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 flex-shrink-0" />
              <span className="text-xs text-yellow-700">{error}</span>
            </div>
            <button onClick={refetch} className="text-xs text-yellow-700 hover:text-yellow-900 underline">Retry</button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Results bar + inline filters ────────────────────────────────── */}
      <div className="bg-white border border-grey-200 rounded-xl overflow-hidden">

        {/* Top bar: results info, filter toggle, per-page, pagination */}
        <div className="px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-xs text-grey-500">
            <span>
              Showing <span className="font-semibold text-darkblue-500">{currentRows.length}</span> of{' '}
              <span className="font-semibold text-darkblue-500">{totalItems}</span> {config.entityName}
              {activeFilterCount > 0 && (
                <span className="text-brightblue-600 ml-1">
                  ({activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied)
                </span>
              )}
            </span>

            <div className="flex items-center gap-1.5">
              <label className="text-grey-400">Show</label>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="border border-grey-300 rounded-lg px-2 py-1 text-xs text-darkblue-500 focus:outline-none focus:ring-2 focus:ring-brightblue-300"
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <label className="text-grey-400">per page</label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {grandTotal && (
              <button
                onClick={() => setShowGrandTotal((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-grey-500 hover:text-darkblue-500 transition-colors"
              >
                {showGrandTotal
                  ? <EyeSlashIcon className="w-4 h-4" />
                  : <EyeIcon className="w-4 h-4" />}
                {showGrandTotal ? 'Hide total' : 'Show total'}
              </button>
            )}

            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg border border-grey-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4 text-darkblue-500" />
              </button>
              <span className="text-xs text-darkblue-500 px-1">
                {currentPage} / {totalPages || 1}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg border border-grey-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-grey-50 transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4 text-darkblue-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Inline filter panel — always visible */}
        <div className="px-4 pb-4 border-t border-grey-100">
            <PerformanceFilters
              filters={filters} onFiltersChange={handleFiltersChange}
              loading={loading || refreshing} dataInfo={dataInfo}
              filterOptions={filterOptions?.data || {}} tableType={tableType} multiSelect
            />
          </div>

        {/* ── Main table ────────────────────────────────────────────────── */}
        <div className="border-t border-grey-200 overflow-x-auto">
          <table className="divide-y divide-grey-200" style={{ minWidth: '900px', width: '100%' }}>

            {/* ── Explicit column widths ─────────────────────────────────── */}
            <colgroup>
              <col style={{ width: '200px' }} />{/* Title        */}
              <col style={{ width: '70px'  }} />{/* Invite sent  */}
              <col style={{ width: '60px'  }} />{/* Yes          */}
              <col style={{ width: '60px'  }} />{/* Maybe        */}
              <col style={{ width: '72px'  }} />{/* Conf%        */}
              <col style={{ width: '60px'  }} />{/* Zoom         */}
              <col style={{ width: '68px'  }} />{/* Attend       */}
              <col style={{ width: '68px'  }} />{/* Inv%         */}
              <col style={{ width: '60px'  }} />{/* Yes%         */}
              <col style={{ width: '68px'  }} />{/* Maybe%       */}
              <col style={{ width: '68px'  }} />{/* Bookings     */}
              <col style={{ width: '72px'  }} />{/* Book Conv%   */}
              <col style={{ width: '72px'  }} />{/* Book/1k      */}
            </colgroup>

            {/* ── Two-row merged header ──────────────────────────────────── */}
            <thead className="bg-darkblue-500">
              {/* Row 1 — section group labels */}
              <tr>
                {/* Title — spans both rows */}
                <th
                  rowSpan={2}
                  onClick={() => handleSort('Title')}
                  className="px-3 py-3 text-left text-xs font-bold text-white uppercase tracking-widest cursor-pointer hover:bg-darkblue-600 select-none align-middle"
                >
                  <div className="flex items-center gap-1">
                    {config.titleLabel}
                    <span className="opacity-70 flex-shrink-0">{getSortIcon('Title')}</span>
                  </div>
                </th>

                {/* Responses — Invite sent + Yes + Maybe + Conf% */}
                <th
                  colSpan={4}
                  className="px-3 py-2 text-center text-xs font-bold text-white uppercase tracking-widest border-l-2 border-darkblue-400 whitespace-nowrap"
                >
                  Responses
                </th>

                {/* Attendance — Zoom + Attendees + Invite% + Yes% + Maybe% */}
                <th
                  colSpan={5}
                  className="px-3 py-2 text-center text-xs font-bold text-white uppercase tracking-widest border-l-2 border-darkblue-400 whitespace-nowrap"
                >
                  Attendance
                </th>

                {/* Bookings — Bookings + Conv% + Per 1k */}
                <th
                  colSpan={3}
                  className="px-3 py-2 text-center text-xs font-bold text-white uppercase tracking-widest border-l-2 border-darkblue-400 whitespace-nowrap"
                >
                  Bookings
                </th>
              </tr>

              {/* Row 2 — abbreviated sub-headers */}
              <tr>
                {[
                  // Responses group
                  { key: 'Invite sent',             label: 'Sent',    border: true  },
                  { key: 'Yes Responses',            label: 'Yes',     border: false },
                  { key: 'Maybe Responses',          label: 'Maybe',   border: false },
                  { key: 'Confirmation conversion',  label: 'Conf%',   border: false },
                  // Attendance group
                  { key: 'Zoom registrations',       label: 'Zoom',    border: true  },
                  { key: 'Attendees',                label: 'Attend',  border: false },
                  { key: 'Invite Conversion',        label: 'Inv%',    border: false },
                  { key: 'Yes Conversion %',         label: 'Yes%',    border: false },
                  { key: 'Maybe Conversion %',       label: 'Maybe%',  border: false },
                  // Bookings group
                  { key: 'Bookings',                 label: 'Total',   border: true  },
                  { key: 'Bookings conversion',      label: 'Conv%',   border: false },
                  { key: 'Bookings per 1k invites',  label: '/1k',     border: false },
                ].map(({ key, label, border }) => (
                  <th
                    key={key}
                    onClick={() => handleSort(key)}
                    className={`px-3 py-2 text-center text-xs font-semibold text-white uppercase tracking-widest opacity-80 cursor-pointer hover:bg-darkblue-600 select-none whitespace-nowrap ${
                      border ? 'border-l-2 border-darkblue-400' : ''
                    }`}
                  >
                    <div className="flex items-center justify-center gap-0.5">
                      {label}
                      <span className="opacity-70 flex-shrink-0">{getSortIcon(key)}</span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-grey-100">
              {currentRows.map((row, i) => (
                <TableRow key={`${row.Title}-${i}`} row={row} columns={displayColumns} index={i} config={config} />
              ))}

              {/* Grand total row */}
              {grandTotal && showGrandTotal && (
                <tr className="bg-darkblue-50 font-bold border-t-2 border-darkblue-200">
                  {displayColumns.map((col) => {
                    let value;
                    let special = false;

                    if (col.key === 'Title') {
                      value = 'Grand Total';
                    } else if (config.hasSpecialFormatting && col.key === 'Invite sent') {
                      const normal = rawData.filter((r) => !r.Title?.toLowerCase().includes('missing'));
                      value = normal.reduce((s, r) => s + parseValue(getFieldValue(r, 'Invite sent'), 'number'), 0).toLocaleString();
                      special = rawData.some((r) => r.Title?.toLowerCase().includes('missing'));
                    } else {
                      value = grandTotal[col.key] || 'N/A';
                    }

                    return (
                      <td
                        key={col.key}
                        className={`px-2 py-2.5 text-xs font-bold text-darkblue-500 ${
                          col.key === 'Title' ? 'text-left' : 'text-center'
                        }`}
                      >
                        <div className="relative group flex items-center justify-center gap-1">
                          <span>{value}</span>
                          {special && col.key === 'Invite sent' && (
                            <>
                              <InformationCircleIcon className="w-3 h-3 text-brightblue-500" />
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10 w-56 bg-darkblue-500 text-white text-xs rounded-lg px-3 py-2 shadow-lg pointer-events-none">
                                {subtitle2}
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-darkblue-500" />
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {error && rawData.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            <span className="text-xs text-yellow-700">{error} (showing cached data)</span>
          </div>
          <button onClick={refetch} className="text-xs text-yellow-700 hover:text-yellow-900 underline">Retry</button>
        </div>
      )}
    </div>
  );
};

export default ReportingTestTable;